export const auth = {
  en: {
    // Login
    login: {
      authFailed: "Authentication failed. Please try again.",
      noProfile: "Your account is not set up. Please contact your administrator for an invitation.",
      accountDisabled: "Your account has been disabled. Please contact your administrator.",
      authError: "Authentication error:",
      noPassword: "Your account is not set up with a password yet. Please use the magic link to log in.",
      checkEmail: "Check Your Email",
      resetEmailSent: "We've sent a password reset link to",
      resetEmailInstructions: "Click the link in your email to reset your password.",
      resetEmailSpam: "Didn't receive the email? Check your spam folder.",
      tryDifferentEmail: "Try a different email",
      enterEmailFirst: "Please enter your email address first",
      welcomeTitle: "Welcome to Solariis",
      welcomeSubtitle: "Strategy, Creativity & Artificial Intelligence for Brands That Inspire.",
      copyright: "2025 Solariis Corp. All rights reserved.",
      signInTitle: "Sign in to your account",
      signInSubtitle: "Access the Solariis Dashboard",
      emailLabel: "Email address",
      emailPlaceholder: "you@example.com",
      passwordLabel: "Password",
      passwordPlaceholder: "Enter your password",
      forgotPassword: "Forgot password?",
      signIn: "Sign In",
      signingIn: "Signing in...",
      needAccess: "Need access?",
      contactAdmin: "Contact your administrator",
      termsNotice: "By signing in, you agree to our Terms of Service and Privacy Policy",
      termsPrefix: "By signing in, you agree to our",
      termsOfService: "Terms of Service",
      and: "and",
      privacyPolicy: "Privacy Policy",
    },
    // Onboarding
    onboarding: {
      creativeLabPartner: "Creative Lab & Strategic Partner",
      passwordMinLength: "Password must be at least 8 characters long",
      passwordLowercase: "Password must contain at least one lowercase letter",
      passwordUppercase: "Password must contain at least one uppercase letter",
      passwordNumber: "Password must contain at least one number",
      passwordsNoMatch: "Passwords do not match",
      enterCompanyName: "Please enter your company name",
      selectIndustry: "Please select your industry",
      title: "Complete your account setup",
      subtitle: "Create a password and tell us about your company",
      passwordRequired: "Password *",
      createPassword: "Create a password",
      confirmPasswordRequired: "Confirm Password *",
      reenterPassword: "Confirm your password",
      companyNameRequired: "Company Name *",
      companyNamePlaceholder: "Enter your company name",
      industryRequired: "Industry *",
      selectIndustryPlaceholder: "Select your industry",
      continueToDashboard: "Continue to Dashboard",
      completingSetup: "Completing setup...",
      settingUp: "Setting up...",
      welcomeRedirecting: "Welcome aboard! Redirecting to dashboard...",
      passwordHint: "Min 8 characters with uppercase, lowercase, and number",
      failedOnboarding: "Failed to complete onboarding",
      failedOnboardingRetry: "Failed to complete onboarding. Please try again.",
      invitedTo: "You've been invited to",
      subtitleInvited: "Create a password to complete your account setup",
    },
    // Magic Link
    magicLink: {
      processing: "Processing authentication...",
      verifying: "Verifying your credentials...",
      timeout: "Authentication took too long. Please try again.",
      acceptingInvitation: "Accepting invitation...",
      settingUpProfile: "Setting up your profile...",
      redirectingDashboard: "Redirecting to dashboard...",
      redirectingLogin: "Redirecting to login...",
      loading: "Loading...",
    },
    // Reset Password
    resetPassword: {
      invalidLink: "Invalid Reset Link",
      linkExpired: "This password reset link is invalid or has expired.",
      returnToLogin: "Return to Login",
      title: "Reset Your Password",
      subtitle: "Enter your new password below",
      newPassword: "New Password",
      newPasswordPlaceholder: "Enter new password",
      confirmPassword: "Confirm Password",
      confirmPasswordPlaceholder: "Confirm new password",
      minChars: "Must be at least 8 characters",
      passwordsNoMatch: "Passwords do not match",
      passwordMinLength: "Password must be at least 8 characters long",
      resetButton: "Reset Password",
      resetting: "Resetting Password...",
      successTitle: "Password Reset Successful",
      successMessage: "Your password has been updated. You will be redirected to the login page.",
      goToLogin: "Go to Login",
      backToLogin: "Back to Login",
    },
    // Talent Onboarding
    talentOnboarding: {
      title: "Talent Network",
      subtitle: "Complete your talent profile",
      description: "Tell us about yourself so we can find the right opportunities",
      passwordRequired: "Password *",
      confirmPasswordRequired: "Confirm Password *",
      passwordHint: "Min 8 characters with uppercase, lowercase, and number",
      resumeRequired: "Resume (PDF) *",
      resumeUploadHint: "Click to upload PDF (max 10MB)",
      expertiseRequired: "Area of Expertise *",
      expertisePlaceholder: "Describe your main skills and expertise (e.g., UI/UX Design, Brand Strategy, Web Development)",
      countryRequired: "Country of Residence *",
      countryPlaceholder: "Select your country",
      workUrls: "Work URLs (optional, max 3)",
      workUrlsHint: "Portfolio, LinkedIn, GitHub, or other relevant links",
      urlPlaceholder: "https://...",
      addUrl: "Add another URL",
      submitProfile: "Submit Profile",
      uploadingResume: "Uploading resume...",
      submittingProfile: "Submitting profile...",
      profileSubmitted: "Profile submitted! Redirecting...",
      invalidPdf: "Please upload a PDF file",
      fileTooLarge: "File size must be less than 10MB",
      pleaseUploadResume: "Please upload your resume",
      pleaseDescribeExpertise: "Please describe your area of expertise",
      pleaseSelectCountry: "Please select your country of residence",
      invalidUrl: "Invalid URL:",
      sessionExpired: "Session expired. Please log in again.",
      failedOnboarding: "Failed to complete onboarding",
      failedOnboardingRetry: "Failed to complete onboarding. Please try again.",
      uploaded: "Uploaded",
      ready: "Ready",
    },
    // Verify
    verify: {
      verifyingMagicLink: "Verifying your magic link...",
      invalidMagicLink: "Invalid magic link. No token provided.",
      loginSuccess: "Login successful! Redirecting...",
      invalidOrExpired: "Invalid or expired magic link. Please request a new one.",
      verificationFailed: "Failed to verify magic link. Please try again.",
      creativeLabPartner: "Creative Lab & Strategic Partner",
      backToLogin: "Back to Login",
      loading: "Loading...",
      verifyingEmail: "Verifying your email...",
      emailVerified: "Email verified successfully!",
    },
    // Invite
    invite: {
      validatingInvitation: "Validating invitation...",
      invalidInvitation: "Invitation Invalid",
      goToLogin: "Go to Login",
      accountCreated: "Account Created!",
      emailLabel: "Email",
      tempPassword: "Temporary Password",
      importantNotice: "Important:",
      savePassword: "Save this password securely. You can change it after logging in.",
      goToLoginPage: "Go to Login Page",
      usePasswordTab: "Use the Password tab on the login page",
      youreInvited: "You're Invited!",
      invitedToJoin: "You've been invited to join",
      thePortalOn: "the",
      portalOnSolariis: "portal on Solariis.",
      creatingAccountFor: "Creating account for:",
      creatingAccount: "Creating Account...",
      acceptAndCreate: "Accept & Create Account",
      generateCredentials: "We'll generate login credentials for you",
      failedToValidate: "Failed to validate invitation",
      failedToCreate: "Failed to create account",
      invalidInvitationGeneric: "Invalid invitation",
      invitationExpired: "This invitation link is invalid or has expired.",
      returnToLogin: "Return to Login",
    },
    // Industries (shared across auth pages)
    industries: {
      technology: "Technology",
      healthcare: "Healthcare",
      finance: "Finance",
      retail: "Retail",
      education: "Education",
      manufacturing: "Manufacturing",
      realEstate: "Real Estate",
      hospitality: "Hospitality",
      foodBeverage: "Food & Beverage",
      professionalServices: "Professional Services",
      nonprofit: "Nonprofit",
      pharmacy: "Pharmacy",
      brand: "Brand",
      distributor: "Distributor",
      agency: "Agency",
      other: "Other",
    },
  },
  es: {
    login: {
      authFailed: "Error de autenticación. Por favor intenta de nuevo.",
      noProfile: "Tu cuenta no está configurada. Contacta a tu administrador para una invitación.",
      accountDisabled: "Tu cuenta ha sido deshabilitada. Contacta a tu administrador.",
      authError: "Error de autenticación:",
      noPassword: "Tu cuenta aún no tiene contraseña configurada. Usa el enlace mágico para iniciar sesión.",
      checkEmail: "Revisa tu correo",
      resetEmailSent: "Hemos enviado un enlace para restablecer tu contraseña a",
      resetEmailInstructions: "Haz clic en el enlace en tu correo para restablecer tu contraseña.",
      resetEmailSpam: "¿No recibiste el correo? Revisa tu carpeta de spam.",
      tryDifferentEmail: "Probar con otro correo",
      enterEmailFirst: "Por favor ingresa tu correo electrónico primero",
      welcomeTitle: "Bienvenido a Solariis",
      welcomeSubtitle: "Estrategia, Creatividad e Inteligencia Artificial para Marcas que Inspiran.",
      copyright: "2025 Solariis Corp. Todos los derechos reservados.",
      signInTitle: "Inicia sesión en tu cuenta",
      signInSubtitle: "Accede al Panel de Solariis",
      emailLabel: "Correo electrónico",
      emailPlaceholder: "tu@ejemplo.com",
      passwordLabel: "Contraseña",
      passwordPlaceholder: "Ingresa tu contraseña",
      forgotPassword: "¿Olvidaste tu contraseña?",
      signIn: "Iniciar Sesión",
      signingIn: "Iniciando sesión...",
      needAccess: "¿Necesitas acceso?",
      contactAdmin: "Contacta a tu administrador",
      termsNotice: "Al iniciar sesión, aceptas nuestros Términos de Servicio y Política de Privacidad",
      termsPrefix: "Al iniciar sesión, aceptas nuestros",
      termsOfService: "Términos de Servicio",
      and: "y",
      privacyPolicy: "Política de Privacidad",
    },
    onboarding: {
      creativeLabPartner: "Laboratorio Creativo y Socio Estratégico",
      passwordMinLength: "La contraseña debe tener al menos 8 caracteres",
      passwordLowercase: "La contraseña debe contener al menos una letra minúscula",
      passwordUppercase: "La contraseña debe contener al menos una letra mayúscula",
      passwordNumber: "La contraseña debe contener al menos un número",
      passwordsNoMatch: "Las contraseñas no coinciden",
      enterCompanyName: "Por favor ingresa el nombre de tu empresa",
      selectIndustry: "Por favor selecciona tu industria",
      title: "Completa la configuración de tu cuenta",
      subtitle: "Crea una contraseña y cuéntanos sobre tu empresa",
      passwordRequired: "Contraseña *",
      createPassword: "Crea una contraseña",
      confirmPasswordRequired: "Confirmar Contraseña *",
      reenterPassword: "Confirma tu contraseña",
      companyNameRequired: "Nombre de Empresa *",
      companyNamePlaceholder: "Ingresa el nombre de tu empresa",
      industryRequired: "Industria *",
      selectIndustryPlaceholder: "Selecciona tu industria",
      continueToDashboard: "Continuar al Panel",
      completingSetup: "Completando configuración...",
      settingUp: "Configurando...",
      welcomeRedirecting: "¡Bienvenido! Redirigiendo al panel...",
      passwordHint: "Mín. 8 caracteres con mayúscula, minúscula y número",
      failedOnboarding: "Error al completar el registro",
      failedOnboardingRetry: "Error al completar el registro. Por favor intenta de nuevo.",
      invitedTo: "Has sido invitado a",
      subtitleInvited: "Crea una contraseña para completar tu cuenta",
    },
    magicLink: {
      processing: "Procesando autenticación...",
      verifying: "Verificando tus credenciales...",
      timeout: "La autenticación tardó demasiado. Por favor intenta de nuevo.",
      acceptingInvitation: "Aceptando invitación...",
      settingUpProfile: "Configurando tu perfil...",
      redirectingDashboard: "Redirigiendo al panel...",
      redirectingLogin: "Redirigiendo al inicio de sesión...",
      loading: "Cargando...",
    },
    resetPassword: {
      invalidLink: "Enlace Inválido",
      linkExpired: "Este enlace de restablecimiento es inválido o ha expirado.",
      returnToLogin: "Volver al Inicio de Sesión",
      title: "Restablece tu Contraseña",
      subtitle: "Ingresa tu nueva contraseña a continuación",
      newPassword: "Nueva Contraseña",
      newPasswordPlaceholder: "Ingresa nueva contraseña",
      confirmPassword: "Confirmar Contraseña",
      confirmPasswordPlaceholder: "Confirma nueva contraseña",
      minChars: "Debe tener al menos 8 caracteres",
      passwordsNoMatch: "Las contraseñas no coinciden",
      passwordMinLength: "La contraseña debe tener al menos 8 caracteres",
      resetButton: "Restablecer Contraseña",
      resetting: "Restableciendo Contraseña...",
      successTitle: "Contraseña Restablecida",
      successMessage: "Tu contraseña ha sido actualizada. Serás redirigido a la página de inicio de sesión.",
      goToLogin: "Ir al Inicio de Sesión",
      backToLogin: "Volver al Inicio de Sesión",
    },
    talentOnboarding: {
      title: "Red de Talento",
      subtitle: "Completa tu perfil de talento",
      description: "Cuéntanos sobre ti para encontrar las oportunidades adecuadas",
      passwordRequired: "Contraseña *",
      confirmPasswordRequired: "Confirmar Contraseña *",
      passwordHint: "Mín. 8 caracteres con mayúscula, minúscula y número",
      resumeRequired: "Currículum (PDF) *",
      resumeUploadHint: "Haz clic para subir PDF (máx. 10MB)",
      expertiseRequired: "Área de Especialización *",
      expertisePlaceholder: "Describe tus principales habilidades y especialización (ej: Diseño UI/UX, Estrategia de Marca, Desarrollo Web)",
      countryRequired: "País de Residencia *",
      countryPlaceholder: "Selecciona tu país",
      workUrls: "URLs de trabajo (opcional, máx. 3)",
      workUrlsHint: "Portafolio, LinkedIn, GitHub u otros enlaces relevantes",
      urlPlaceholder: "https://...",
      addUrl: "Agregar otra URL",
      submitProfile: "Enviar Perfil",
      uploadingResume: "Subiendo currículum...",
      submittingProfile: "Enviando perfil...",
      profileSubmitted: "¡Perfil enviado! Redirigiendo...",
      invalidPdf: "Por favor sube un archivo PDF",
      fileTooLarge: "El archivo debe ser menor a 10MB",
      pleaseUploadResume: "Por favor sube tu currículum",
      pleaseDescribeExpertise: "Por favor describe tu área de especialización",
      pleaseSelectCountry: "Por favor selecciona tu país de residencia",
      invalidUrl: "URL inválida:",
      sessionExpired: "Sesión expirada. Por favor inicia sesión de nuevo.",
      failedOnboarding: "Error al completar el registro",
      failedOnboardingRetry: "Error al completar el registro. Por favor intenta de nuevo.",
      uploaded: "Subido",
      ready: "Listo",
    },
    verify: {
      verifyingMagicLink: "Verificando tu enlace mágico...",
      invalidMagicLink: "Enlace mágico inválido. No se proporcionó token.",
      loginSuccess: "¡Inicio de sesión exitoso! Redirigiendo...",
      invalidOrExpired: "Enlace mágico inválido o expirado. Por favor solicita uno nuevo.",
      verificationFailed: "Error al verificar el enlace mágico. Por favor intenta de nuevo.",
      creativeLabPartner: "Laboratorio Creativo y Socio Estratégico",
      backToLogin: "Volver al Inicio de Sesión",
      loading: "Cargando...",
      verifyingEmail: "Verificando tu correo...",
      emailVerified: "¡Correo verificado exitosamente!",
    },
    invite: {
      validatingInvitation: "Validando invitación...",
      invalidInvitation: "Invitación Inválida",
      goToLogin: "Ir al Inicio de Sesión",
      accountCreated: "¡Cuenta Creada!",
      emailLabel: "Correo electrónico",
      tempPassword: "Contraseña Temporal",
      importantNotice: "Importante:",
      savePassword: "Guarda esta contraseña de forma segura. Puedes cambiarla después de iniciar sesión.",
      goToLoginPage: "Ir a la Página de Inicio de Sesión",
      usePasswordTab: "Usa la pestaña de Contraseña en la página de inicio de sesión",
      youreInvited: "¡Estás Invitado!",
      invitedToJoin: "Has sido invitado a unirte a",
      thePortalOn: "el",
      portalOnSolariis: "portal en Solariis.",
      creatingAccountFor: "Creando cuenta para:",
      creatingAccount: "Creando Cuenta...",
      acceptAndCreate: "Aceptar y Crear Cuenta",
      generateCredentials: "Generaremos credenciales de inicio de sesión para ti",
      failedToValidate: "Error al validar la invitación",
      failedToCreate: "Error al crear la cuenta",
      invalidInvitationGeneric: "Invitación inválida",
      invitationExpired: "Este enlace de invitación es inválido o ha expirado.",
      returnToLogin: "Volver al Inicio de Sesión",
    },
    industries: {
      technology: "Tecnología",
      healthcare: "Salud",
      finance: "Finanzas",
      retail: "Retail",
      education: "Educación",
      manufacturing: "Manufactura",
      realEstate: "Bienes Raíces",
      hospitality: "Hospitalidad",
      foodBeverage: "Alimentos y Bebidas",
      professionalServices: "Servicios Profesionales",
      nonprofit: "Sin Fines de Lucro",
      pharmacy: "Farmacia",
      brand: "Marca",
      distributor: "Distribuidor",
      agency: "Agencia",
      other: "Otro",
    },
  },
};
